/**
 * Chart Registry — central config per chart (Group A).
 * Drives tab rendering, controls, tooltips, and empty/error copy.
 */

import { getMetric } from "./metricRegistry";

export const CHART_TYPES = {
  BAR: "bar",
  LINE: "line",
  MULTI_LINE: "multi_line",
  BADGE: "badge",
  COMPARISON: "comparison",
  SCATTER: "scatter",
  INDEXED_DUAL_LINE: "indexed_dual_line",
  STAT_CHIP: "stat_chip",
  PRICE_DMA: "price_dma",
  DRAWDOWN: "drawdown",
  RSI_MINI: "rsi_mini",
};

export const SUPPORTED_CONTROLS = {
  PERIOD: "period",
  RANGE: "range",
  SHOW_ESTIMATES: "showEstimates",
  SHOW_YOY: "showYoY",
  VALUE_MODE: "valueMode",
  CAGR: "cagr",
  SHOW_MEDIAN: "showMedian",
};

const DEFAULT_CHART_DEFAULTS = {
  period: "quarter",
  range: "5Y",
  showEstimates: true,
  showYoY: false,
  valueMode: "abs",
};

function chartDef({
  id,
  tab,
  title,
  metricId,
  metricIds,
  dataSource,
  chartType = CHART_TYPES.BAR,
  defaults = {},
  supportedControls,
  emptyCopy,
  errorCopy,
  isPercent,
}) {
  const ids = metricIds || (metricId ? [metricId] : []);
  const primary = getMetric(ids[0]);
  return {
    id,
    tab,
    title: title || primary?.label || ids[0] || id,
    metricIds: ids,
    dataSource,
    chartType,
    defaults: { ...DEFAULT_CHART_DEFAULTS, ...defaults },
    supportedControls: supportedControls || [
      SUPPORTED_CONTROLS.PERIOD,
      SUPPORTED_CONTROLS.RANGE,
      SUPPORTED_CONTROLS.SHOW_ESTIMATES,
      SUPPORTED_CONTROLS.SHOW_YOY,
    ],
    tooltipIds: ids.map((mid) => getMetric(mid)?.tooltipId || mid),
    emptyCopy: emptyCopy || `No ${title || primary?.label || "metric"} data for this period range.`,
    errorCopy: errorCopy || `${title || primary?.label || "Metric"} data could not be loaded.`,
    isPercent: isPercent ?? Boolean(primary?.isPercent),
    displayOnly: Boolean(primary?.displayOnly),
    perShare: Boolean(primary?.perShare),
    kind: primary?.kind,
  };
}

function multiLineChartDef(opts) {
  return chartDef({
    ...opts,
    chartType: CHART_TYPES.MULTI_LINE,
    supportedControls: opts.supportedControls || [
      SUPPORTED_CONTROLS.PERIOD,
      SUPPORTED_CONTROLS.RANGE,
    ],
  });
}

/** @type {Record<string, object>} */
export const CHART_REGISTRY = {
  "financials.revenue": chartDef({
    id: "financials.revenue",
    tab: "financials",
    metricId: "revenue",
    dataSource: "series.financials",
  }),
  "financials.gross_profit": chartDef({
    id: "financials.gross_profit",
    tab: "financials",
    metricId: "gross_profit",
    dataSource: "series.financials",
  }),
  "financials.operating_income": chartDef({
    id: "financials.operating_income",
    tab: "financials",
    metricId: "operating_income",
    dataSource: "series.financials",
  }),
  "financials.net_income": chartDef({
    id: "financials.net_income",
    tab: "financials",
    metricId: "net_income",
    dataSource: "series.financials",
  }),
  "financials.eps_diluted": chartDef({
    id: "financials.eps_diluted",
    tab: "financials",
    metricId: "eps_diluted",
    dataSource: "series.financials",
  }),
  "financials.ebitda": chartDef({
    id: "financials.ebitda",
    tab: "financials",
    metricId: "ebitda",
    dataSource: "series.financials",
  }),
  "cashflow.operating_cash_flow": chartDef({
    id: "cashflow.operating_cash_flow",
    tab: "cashflow",
    metricId: "operating_cash_flow",
    dataSource: "series.cashflow",
  }),
  "cashflow.capital_expenditure": chartDef({
    id: "cashflow.capital_expenditure",
    tab: "cashflow",
    metricId: "capital_expenditure",
    dataSource: "series.cashflow",
  }),
  "cashflow.free_cash_flow": chartDef({
    id: "cashflow.free_cash_flow",
    tab: "cashflow",
    metricId: "free_cash_flow",
    dataSource: "series.cashflow",
  }),
  "cashflow.fcf_margin": chartDef({
    id: "cashflow.fcf_margin",
    tab: "cashflow",
    metricId: "fcf_margin",
    dataSource: "series.cashflow",
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE, SUPPORTED_CONTROLS.SHOW_YOY],
  }),
  "cashflow.fcf_per_share": chartDef({
    id: "cashflow.fcf_per_share",
    tab: "cashflow",
    metricId: "fcf_per_share",
    dataSource: "series.cashflow",
  }),
  "cashflow.custom_fcf": chartDef({
    id: "cashflow.custom_fcf",
    tab: "cashflow",
    metricId: "custom_fcf",
    dataSource: "series.cashflow",
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE],
    emptyCopy: "Custom FCF needs operating cash flow and capex for the latest period.",
  }),
  "margins.trends": multiLineChartDef({
    id: "margins.trends",
    tab: "margins",
    title: "Margin Trends",
    metricIds: ["gross_margin", "operating_margin", "net_margin", "fcf_margin"],
    dataSource: "series.margins",
    isPercent: true,
    emptyCopy: "Margin trends need income statement and cash flow history.",
  }),
  "quality.returns_moat": multiLineChartDef({
    id: "quality.returns_moat",
    title: "Quality & Moat",
    tab: "quality",
    metricIds: ["roic", "roe", "roa", "fcf_conversion", "reinvestment_rate", "rnd_pct_revenue", "sga_pct_revenue"],
    dataSource: "series.quality",
    isPercent: true,
    emptyCopy: "Quality metrics need key-metrics and statement history.",
  }),
  "balance.levels": multiLineChartDef({
    id: "balance.levels",
    tab: "balance",
    title: "Cash & Debt Levels",
    metricIds: ["cash", "debt", "net_debt"],
    dataSource: "series.balance",
    isPercent: false,
    emptyCopy: "Balance sheet levels need balance-sheet statement history.",
  }),
  "balance.leverage": multiLineChartDef({
    id: "balance.leverage",
    tab: "balance",
    title: "Leverage & Coverage",
    metricIds: ["net_debt_to_ebitda", "interest_coverage"],
    dataSource: "series.balance",
    isPercent: false,
    emptyCopy: "Leverage ratios need balance sheet and key-metrics data.",
  }),
  "balance.structural_scores": {
    id: "balance.structural_scores",
    tab: "balance",
    title: "Structural Health",
    metricIds: ["piotroski_score", "altman_z_score"],
    dataSource: "financialScores",
    chartType: CHART_TYPES.BADGE,
    defaults: DEFAULT_CHART_DEFAULTS,
    supportedControls: [],
    tooltipIds: ["piotroski_score", "altman_z_score"],
    emptyCopy: "Structural scores need financial-scores data from FMP.",
    errorCopy: "Structural scores could not be loaded.",
    isPercent: false,
  },
  "growth.revenue_growth": chartDef({
    id: "growth.revenue_growth",
    tab: "growth",
    metricId: "revenue_growth",
    dataSource: "series.growth",
    isPercent: true,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE, SUPPORTED_CONTROLS.CAGR],
  }),
  "growth.eps_growth": chartDef({
    id: "growth.eps_growth",
    tab: "growth",
    metricId: "eps_growth",
    dataSource: "series.growth",
    isPercent: true,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE, SUPPORTED_CONTROLS.CAGR],
  }),
  "growth.fcf_growth": chartDef({
    id: "growth.fcf_growth",
    tab: "growth",
    metricId: "fcf_growth",
    dataSource: "series.growth",
    isPercent: true,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE, SUPPORTED_CONTROLS.CAGR],
  }),
  "growth.gross_profit_growth": chartDef({
    id: "growth.gross_profit_growth",
    tab: "growth",
    metricId: "gross_profit_growth",
    dataSource: "series.growth",
    isPercent: true,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE, SUPPORTED_CONTROLS.CAGR],
  }),
  "growth.operating_income_growth": chartDef({
    id: "growth.operating_income_growth",
    tab: "growth",
    metricId: "operating_income_growth",
    dataSource: "series.growth",
    isPercent: true,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE, SUPPORTED_CONTROLS.CAGR],
  }),
  "growth.revenue_per_share": chartDef({
    id: "growth.revenue_per_share",
    tab: "growth",
    metricId: "revenue_per_share",
    dataSource: "series.growth",
    isPercent: false,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE, SUPPORTED_CONTROLS.VALUE_MODE],
  }),
  "growth.fcf_per_share": chartDef({
    id: "growth.fcf_per_share",
    tab: "growth",
    title: "FCF / Share",
    metricId: "fcf_per_share",
    dataSource: "series.growth",
    isPercent: false,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE],
  }),
  "growth.book_value_per_share": chartDef({
    id: "growth.book_value_per_share",
    tab: "growth",
    metricId: "book_value_per_share",
    dataSource: "series.growth",
    isPercent: false,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE],
  }),
  "growth.eps_revision_trend": chartDef({
    id: "growth.eps_revision_trend",
    tab: "growth",
    metricId: "eps_revision_trend",
    dataSource: "series.growth",
    isPercent: true,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE],
    emptyCopy: "EPS revision trend needs analyst estimate history.",
  }),
  "valuation.multiples_pe": multiLineChartDef({
    id: "valuation.multiples_pe",
    tab: "valuation",
    title: "P/E — Trailing & Forward",
    metricIds: ["trailing_pe", "forward_pe"],
    dataSource: "series.valuation",
    isPercent: false,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE, SUPPORTED_CONTROLS.SHOW_MEDIAN],
  }),
  "valuation.multiples_ev": multiLineChartDef({
    id: "valuation.multiples_ev",
    tab: "valuation",
    title: "EV Multiples",
    metricIds: ["ev_to_sales", "ev_to_ebitda"],
    dataSource: "series.valuation",
    isPercent: false,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE, SUPPORTED_CONTROLS.SHOW_MEDIAN],
  }),
  "valuation.multiples_yield": multiLineChartDef({
    id: "valuation.multiples_yield",
    tab: "valuation",
    title: "Yield & PEG",
    metricIds: ["price_to_free_cash_flow", "fcf_yield", "earnings_yield", "peg"],
    dataSource: "series.valuation",
    isPercent: false,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE, SUPPORTED_CONTROLS.SHOW_MEDIAN],
  }),
  "valuation.ath_distance": {
    id: "valuation.ath_distance",
    tab: "valuation",
    title: "Distance from ATH",
    metricIds: ["distance_from_ath"],
    dataSource: "priceContext",
    chartType: CHART_TYPES.STAT_CHIP,
    defaults: DEFAULT_CHART_DEFAULTS,
    supportedControls: [SUPPORTED_CONTROLS.RANGE],
    tooltipIds: ["distance_from_ath"],
    emptyCopy: "ATH distance needs live quote and price history.",
    isPercent: false,
  },
  "valuation.price_vs_200dma": {
    id: "valuation.price_vs_200dma",
    tab: "valuation",
    title: "Price vs 200DMA",
    metricIds: ["price", "dma200"],
    dataSource: "priceAnalytics",
    chartType: CHART_TYPES.PRICE_DMA,
    defaults: DEFAULT_CHART_DEFAULTS,
    supportedControls: [SUPPORTED_CONTROLS.RANGE],
    tooltipIds: ["price", "dma200"],
    emptyCopy: "Price vs 200DMA needs historical prices.",
    isPercent: false,
  },
  "valuation.drawdown": {
    id: "valuation.drawdown",
    tab: "valuation",
    title: "Drawdown",
    metricIds: ["drawdown_pct"],
    dataSource: "priceAnalytics",
    chartType: CHART_TYPES.DRAWDOWN,
    defaults: DEFAULT_CHART_DEFAULTS,
    supportedControls: [SUPPORTED_CONTROLS.RANGE],
    tooltipIds: ["drawdown_pct"],
    emptyCopy: "Drawdown chart needs historical prices.",
    isPercent: true,
  },
  "valuation.rsi": {
    id: "valuation.rsi",
    tab: "valuation",
    title: "RSI (14)",
    metricIds: ["rsi_14"],
    dataSource: "priceAnalytics",
    chartType: CHART_TYPES.RSI_MINI,
    defaults: DEFAULT_CHART_DEFAULTS,
    supportedControls: [SUPPORTED_CONTROLS.RANGE],
    tooltipIds: ["rsi_14"],
    emptyCopy: "RSI needs at least 15 price observations.",
    isPercent: false,
  },
  "capital.shares_outstanding": chartDef({
    id: "capital.shares_outstanding",
    tab: "capital",
    metricId: "shares_outstanding",
    dataSource: "series.capital",
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE],
    emptyCopy: "Shares outstanding needs income-statement share count history.",
  }),
  "capital.stock_based_compensation": chartDef({
    id: "capital.stock_based_compensation",
    tab: "capital",
    metricId: "stock_based_compensation",
    dataSource: "series.capital",
    emptyCopy: "Stock-based compensation needs cashflow-statement history.",
  }),
  "capital.share_buybacks": chartDef({
    id: "capital.share_buybacks",
    tab: "capital",
    title: "Share Buybacks",
    metricId: "share_buybacks",
    dataSource: "series.capital",
    emptyCopy: "Buyback data needs cashflow-statement.commonStockRepurchased.",
  }),
  "capital.dividends_paid": chartDef({
    id: "capital.dividends_paid",
    tab: "capital",
    metricId: "dividends_paid",
    dataSource: "series.capital",
    emptyCopy: "Dividend data needs cashflow-statement.commonDividendsPaid.",
  }),
  "capital.acquisitions_net": chartDef({
    id: "capital.acquisitions_net",
    tab: "capital",
    metricId: "acquisitions_net",
    dataSource: "series.capital",
    emptyCopy: "Acquisition data needs cashflow-statement.acquisitionsNet.",
  }),
  "comparison.growth_vs_pe": {
    id: "comparison.growth_vs_pe",
    tab: "comparisons",
    title: "Growth vs P/E",
    metricIds: ["growth_vs_pe"],
    dataSource: "comparisons.growth_vs_pe",
    chartType: CHART_TYPES.SCATTER,
    defaults: DEFAULT_CHART_DEFAULTS,
    supportedControls: [SUPPORTED_CONTROLS.PERIOD, SUPPORTED_CONTROLS.RANGE],
    tooltipIds: ["growth_vs_pe"],
    emptyCopy: "Growth vs P/E needs EPS growth and trailing P/E history.",
    errorCopy: "Growth vs P/E data could not be loaded.",
    isPercent: false,
    displayOnly: true,
  },
  "comparison.price_vs_fcf_growth": {
    id: "comparison.price_vs_fcf_growth",
    tab: "comparisons",
    title: "Price vs FCF Growth",
    metricIds: ["price_vs_fcf_growth"],
    dataSource: "comparisons.price_vs_fcf_growth",
    chartType: CHART_TYPES.INDEXED_DUAL_LINE,
    defaults: DEFAULT_CHART_DEFAULTS,
    supportedControls: [SUPPORTED_CONTROLS.RANGE],
    tooltipIds: ["price_vs_fcf_growth"],
    emptyCopy: "Price vs FCF growth needs price history and FCF statement history.",
    errorCopy: "Price vs FCF growth data could not be loaded.",
    isPercent: false,
    displayOnly: true,
  },
};

export const COMPANY_METRICS_TABS = [
  { id: "financials", label: "Financials", chartIds: Object.keys(CHART_REGISTRY).filter((id) => id.startsWith("financials.")) },
  { id: "cashflow", label: "Cash Flow", chartIds: Object.keys(CHART_REGISTRY).filter((id) => id.startsWith("cashflow.")) },
  { id: "margins", label: "Margins", chartIds: Object.keys(CHART_REGISTRY).filter((id) => id.startsWith("margins.")) },
  { id: "quality", label: "Quality", chartIds: Object.keys(CHART_REGISTRY).filter((id) => id.startsWith("quality.")) },
  { id: "balance", label: "Balance Sheet", chartIds: Object.keys(CHART_REGISTRY).filter((id) => id.startsWith("balance.")) },
  { id: "growth", label: "Growth", chartIds: Object.keys(CHART_REGISTRY).filter((id) => id.startsWith("growth.")) },
  { id: "valuation", label: "Valuation", chartIds: Object.keys(CHART_REGISTRY).filter((id) => id.startsWith("valuation.")) },
  { id: "capital", label: "Capital Allocation", chartIds: Object.keys(CHART_REGISTRY).filter((id) => id.startsWith("capital.")) },
  {
    id: "comparisons",
    label: "Comparisons",
    sectionTitle: "Raw Comparisons",
    chartIds: Object.keys(CHART_REGISTRY).filter((id) => id.startsWith("comparison.")),
  },
];

export function getChart(chartId) {
  return CHART_REGISTRY[chartId] || null;
}

export function getAllCharts() {
  return Object.values(CHART_REGISTRY);
}

export function getTabMeta(tabId) {
  return COMPANY_METRICS_TABS.find((t) => t.id === tabId) || null;
}

export function getChartsForTab(tabId) {
  const tab = COMPANY_METRICS_TABS.find((t) => t.id === tabId);
  if (!tab) return [];
  return tab.chartIds.map((id) => CHART_REGISTRY[id]).filter(Boolean);
}

export function getSeriesKeyForChart(chart) {
  if (!chart?.dataSource) return null;
  if (chart.dataSource.startsWith("series.")) {
    return chart.dataSource.split(".")[1];
  }
  return chart.dataSource;
}
